import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

class VanityMenuLatest extends HTMLElement {
  static requests = new Map();
  connectedCallback() {
    this.drawer = this.closest('details');
    this.onToggle = () => {
      if (this.drawer?.open) this.load();
    };
    this.drawer?.addEventListener('toggle', this.onToggle);
    this.onToggle();
  }
  disconnectedCallback() {
    this.drawer?.removeEventListener('toggle', this.onToggle);
  }
  async page(url) {
    const key = url.toString();
    if (!VanityMenuLatest.requests.has(key)) {
      VanityMenuLatest.requests.set(key, fetch(key).then(response => {
        if (!response.ok) throw new Error('Unable to load category');
        return response.text();
      }).catch(error => {
        VanityMenuLatest.requests.delete(key);
        throw error;
      }));
    }
    return new DOMParser().parseFromString(await VanityMenuLatest.requests.get(key), 'text/html');
  }
  family(card) {
    const title = (card.dataset.title || card.querySelector('.resource-card__title')?.textContent || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    if (!title.trim()) return card.dataset.productId;
    const hasSize = /\b(grande|grandi|piccolo|piccola|piccoli|piccole|medio|media|mini|small|medium|large|maxi|formato|dimensioni)\b/.test(title);
    let family = title.replace(/\b(grande|grandi|piccolo|piccola|piccoli|piccole|medio|media|mini|small|medium|large|maxi)\b/g, '');
    if (hasSize) family = family.replace(/\b[a-z]*\d[\w.-]*\b/g, '');
    return family.replace(/[^a-z0-9]+/g, ' ').trim();
  }
  priority(slot) {
    const category = slot.dataset.category || '';
    if (category === 'outlet') return -1;
    if (category.includes('zaini')) return 0;
    if (category.includes('abbigliamento')) return 1;
    if (category.includes('borse')) return 2;
    if (category.includes('valigeria')) return 3;
    if (category.includes('accessori')) return 4;
    return 5;
  }
  async load() {
    if (this.loading || this.loaded) return;
    this.loading = true;
    const slots = [...this.querySelectorAll('[data-collection-url]')];
    const usedIds = new Set();
    const usedFamilies = new Set();
    const initial = await Promise.all(slots.map(async slot => {
      const url = new URL(slot.dataset.collectionUrl, window.location.origin);
      url.search = new URLSearchParams({sort_by: 'created-descending', section_id: 'vanity-menu-latest'}).toString();
      try { return {slot, url, parsed: await this.page(url)}; }
      catch (error) { console.warn('Vanity menu category unavailable', error); return {slot, error}; }
    }));
    for (const entry of initial.sort((a, b) => this.priority(a.slot) - this.priority(b.slot))) {
      const {slot, url} = entry;
      if (entry.error) continue;
      try {
        let parsed = entry.parsed;
        let card = parsed.querySelector('[data-vanity-latest-card]');
        if (slot.dataset.category === 'outlet') {
          let next = parsed.querySelector('[data-vanity-outlet-page]')?.dataset.nextPage;
          const seen = new Set([url.toString()]);
          while (next) {
            const nextUrl = new URL(next, window.location.origin);
            nextUrl.searchParams.set('section_id', 'vanity-menu-latest');
            if (seen.has(nextUrl.toString())) break;
            seen.add(nextUrl.toString());
            parsed = await this.page(nextUrl);
            const candidate = parsed.querySelector('[data-vanity-latest-card]');
            if (candidate && (!card ||
                Number(candidate.dataset.saving) * Number(card.dataset.compare) > Number(card.dataset.saving) * Number(candidate.dataset.compare) ||
                (Number(candidate.dataset.saving) * Number(card.dataset.compare) === Number(card.dataset.saving) * Number(candidate.dataset.compare) &&
                 candidate.dataset.title.localeCompare(card.dataset.title, 'it') < 0))) card = candidate;
            next = parsed.querySelector('[data-vanity-outlet-page]')?.dataset.nextPage;
          }
        } else {
          let page = 1;
          const seen = new Set();
          while (card && (usedIds.has(card.dataset.productId) || usedFamilies.has(this.family(card)))) {
            if (seen.has(card.dataset.productId)) { card = null; break; }
            seen.add(card.dataset.productId);
            url.searchParams.set('page', String(++page));
            parsed = await this.page(url);
            card = parsed.querySelector('[data-vanity-latest-card]');
          }
        }
        if (card) {
          usedIds.add(card.dataset.productId);
          const family = this.family(card);
          usedFamilies.add(family);
          slot.replaceChildren(...card.childNodes);
          slot.dataset.productId = card.dataset.productId;
          slot.dataset.createdAt = card.dataset.createdAt;
          slot.dataset.modelFamily = family;
          slot.hidden = false;
        } else {
          slot.replaceChildren();
          slot.hidden = true;
        }
        slot.dataset.loaded = 'true';
      } catch (error) {
        console.warn('Vanity menu category unavailable', error);
      }
    }
    this.loaded = slots.every(slot => slot.dataset.loaded === 'true');
    this.loading = false;
  }
}
if (!customElements.get('vanity-menu-latest')) {
  customElements.define('vanity-menu-latest', VanityMenuLatest);
}
